import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import AddToInvestorListModal from 'app/components/AddToInvestorListModal';
import RemoveFromInvestorListModal from 'app/components/RemoveFromInvestorListModal';
import { fetchInvestorSavedGroups } from 'data/investors/investorProfileService';

// Add/Remove-from-Investor-List — "Investor Outreach - User Flow" board
// (node 13:24922 area). Shared by the Profile header's bookmark and the
// search result card's bookmark so both stay in sync rather than
// hand-duplicating the same modal-open logic.
//
// ATAI-180: now wired to the real save endpoint via the dedicated
// AddToInvestorListModal (PUT .../new/investor-groups on propstream-be, PR
// #1662) — this was previously a UI-only placeholder ahead of S10 that just
// flipped local state with no API call; that stub is why clicking this
// bookmark never actually saved anything.
//
// `priority` defaults to AddToInvestorListModal's own MEDIUM tier; pass
// Priority.HIGHEST when this is rendered inside another already-open MEDIUM
// popup (e.g. the Investor Profile modal) — PopupHolder only ever renders
// the front of a priority queue, so a same-tier popup would queue silently
// behind it and never show.
//
// `onToggle` (threaded down from a caller like Profile/index.js, same
// contract as PropertyTable's `onPropertyDetailToggle`) tells an outer modal
// to stop treating a click as "outside itself" while this popup is open —
// this modal is its own stacked PopupLayerHolder, a separate DOM subtree
// from the outer modal's ref, so a click inside it (e.g. the list dropdown)
// reads as "outside" the outer modal too and would close both at once.
const useSaveInvestor = (investor, { priority, onToggle } = {}) => {
  const dispatch = useDispatch();
  // ATAI-210: `saved: true` is what the Contacts Investor List route passes
  // (a thin {id, contactId} object with no savedGroups -- an investor reached
  // from that list is saved by definition, see Contacts/InvestorProfileRoute),
  // so without it the bookmark showed as unsaved for an already-saved investor.
  const hasSavedGroups =
    (Array.isArray(investor.savedGroups) && investor.savedGroups.length > 0) ||
    investor.saved === true;
  const [saved, setSaved] = useState(hasSavedGroups);

  // Re-sync when the server-side saved state changes (e.g. a re-search
  // returns updated savedGroups while React reuses this component instance).
  useEffect(() => {
    setSaved(hasSavedGroups);
  }, [hasSavedGroups]);

  const handleAdd = () => {
    onToggle?.(true);
    dispatch(
      AddToInvestorListModal.open({
        investors: [investor],
        ...(priority ? { priority } : {}),
        onSaved: () => setSaved(true),
        onClose: () => onToggle?.(false),
      })
    );
  };

  // ATAI-186: the real "Remove from Investor List?" modal, listing the
  // investor's current list memberships as checkboxes and removing exactly
  // the ones the user unchecks. `onRemove` gets only the lists that actually
  // succeeded (some may fail independently — see RemoveFromInvestorListModal),
  // so `saved` reflects whether the investor is still in any list at all,
  // not just whether Remove was clicked.
  //
  // Always re-fetches savedGroups fresh (fetchInvestorSavedGroups, a
  // lightweight sibling of the full profile fetch) rather than trusting
  // whatever `investor.savedGroups` the caller already had: the S2 search
  // response is fetched once and never self-updates, so it's stale the
  // moment any list membership changes — missing contactId entirely on a
  // first save, or still listing an already-removed group after a partial
  // removal from a multi-list investor. PopupHolder also snapshots
  // `investor` once at open time (see RemoveFromInvestorListModal), so
  // passing in stale data means it's stuck that way for the modal's whole
  // lifetime — there's no "it'll catch up later".
  const handleRemove = async () => {
    onToggle?.(true);

    let freshInvestor = investor;
    try {
      const { data } = await fetchInvestorSavedGroups(investor.id);
      freshInvestor = { ...investor, savedGroups: data };
    } catch (error) {
      // Best effort — RemoveFromInvestorListModal still shows its own
      // "still loading, try again" message rather than acting on stale data.
      console.warn('Could not refresh saved groups before opening Remove:', error);
    }

    dispatch(
      RemoveFromInvestorListModal.open({
        investor: freshInvestor,
        ...(priority ? { priority } : {}),
        onRemove: (removedLists) => {
          const remaining = (freshInvestor.savedGroups?.length || 0) - removedLists.length;
          setSaved(remaining > 0);
        },
        onClose: () => onToggle?.(false),
      })
    );
  };

  const toggleSave = () => (saved ? handleRemove() : handleAdd());

  return { saved, toggleSave };
};

export default useSaveInvestor;
